import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Grid, Card, CardContent, Stack, Button, LinearProgress, Chip, Paper } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EditIcon from '@mui/icons-material/Edit';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { useStudyPlans } from '../context/StudyPlansContext';
import seed from '../data/studyPathsSeedDetailed';

const UI_TOKENS = {
  heroGradient: 'linear-gradient(135deg, #2D5BFF 0%, #1A3BB2 100%)',
  heroChipBg: 'rgba(255,255,255,0.15)',
  heroChipColor: '#fff',
  radius: 8,
  primaryButton: {
    borderRadius: 8,
    fontWeight: 700,
    textTransform: 'none',
  },
};

function SubjectTile({ subject, locked, percent, onOpen }) {
  const topics = Array.isArray(subject?.topics) ? subject.topics : [];
  const est = topics.reduce((s, t) => s + (t.estimatedMinutes || 0), 0);
  const isCompleted = percent === 100;
  
  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      borderRadius: UI_TOKENS.radius,
      border: '2px solid',
      borderColor: locked ? 'action.disabled' : isCompleted ? 'success.main' : 'divider',
      opacity: locked ? 0.7 : 1,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: locked ? 'none' : 'translateY(-4px)',
        boxShadow: locked ? 'none' : '0 8px 24px rgba(0,0,0,0.12)',
        borderColor: locked ? 'action.disabled' : isCompleted ? 'success.dark' : 'primary.main',
      }
    }}>
      <CardContent sx={{ flex: 1, p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: locked ? 'text.disabled' : 'text.primary' }}>
              {subject?.title}
            </Typography>
            {locked ? (
              <Chip 
                icon={<LockIcon />}
                label="Locked" 
                size="small" 
                sx={{ bgcolor: 'action.disabledBackground' }}
              />
            ) : isCompleted ? (
              <Chip 
                icon={<CheckCircleIcon />}
                label="Done!" 
                size="small" 
                color="success"
                sx={{ fontWeight: 700 }}
              />
            ) : (
              <Chip 
                label={`${Math.round(percent)}%`} 
                size="small" 
                color="primary"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
          
          <Stack direction="row" spacing={1.5}>
            <Chip 
              label={`${topics.length} topics`}
              size="small"
              variant="outlined"
              sx={{ borderRadius: '16px' }}
            />
            <Chip 
              label={`${est} mins`}
              size="small"
              variant="outlined"
              sx={{ borderRadius: '16px' }}
            />
          </Stack>

          <Box sx={{ mt: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Progress
              </Typography>
              {percent > 0 && !locked && (
                <Typography variant="caption" sx={{ 
                  color: isCompleted ? 'success.main' : 'primary.main',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  {isCompleted ? '🎉' : <TrendingUpIcon sx={{ fontSize: 14 }} />}
                  {isCompleted ? 'Complete!' : 'In Progress'}
                </Typography>
              )}
            </Stack>
            <LinearProgress 
              variant="determinate" 
              value={locked ? 0 : percent}
              sx={{ 
                height: 8,
                borderRadius: '16px',
                backgroundColor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: '16px',
                  background: isCompleted 
                    ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                    : 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
                }
              }}
            />
          </Box>
        </Stack>
      </CardContent>
      <Box sx={{ p: 2, pt: 0 }}>
        <Button 
          fullWidth
          size="large"
          variant={locked ? "outlined" : "contained"}
          onClick={() => onOpen(subject?.id || subject?.title)} 
          disabled={locked}
          sx={{
            borderRadius: '16px',
            fontWeight: 700,
            py: 1.5,
            textTransform: 'none',
            ...(locked ? {} : {
              boxShadow: '0 4px 14px 0 rgba(25,118,210,0.39)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(25,118,210,0.5)',
              }
            })
          }}
        >
          {locked ? '🔒 Locked' : isCompleted ? '✅ Review' : 'Start Learning'}
        </Button>
        {locked && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Complete previous subject first
          </Typography>
        )}
      </Box>
    </Card>
  );
}

export default function StudyPathOverview() {
  const { pathId } = useParams();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const { getTrack } = useStudyPlans();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    function load() {
      setLoading(true);
      const cached = getTrack(pathId);
      if (cached) {
        // some responses use `subjects`, some use `orderedSubjects` — normalize to `orderedSubjects`
        const ordered = Array.isArray(cached.orderedSubjects) ? cached.orderedSubjects : (Array.isArray(cached.subjects) ? cached.subjects : []);
        const normalized = { ...cached, orderedSubjects: ordered };
        setPath(normalized);
        setProgress(cached.progress || null);
      } else {
        const seedPath = seed.find(s => s.id === pathId);
        const ordered = seedPath ? (Array.isArray(seedPath.orderedSubjects) ? seedPath.orderedSubjects : (Array.isArray(seedPath.subjects) ? seedPath.subjects : [])) : [];
        const normalized = seedPath ? { ...seedPath, orderedSubjects: ordered } : null;
        setPath(normalized);
        setProgress(null);
      }
      if (mounted) setLoading(false);
    }
    load();
    return () => { mounted = false; };
  }, [pathId, getTrack]);

  if (loading) return <Box sx={{ mt: 8, textAlign: 'center' }}>Loading…</Box>;
  if (!path) return <Container sx={{ mt: 8 }}><Typography>No study path found.</Typography></Container>;

  function subjectProgressPercent(subjectId) {
    if (!progress) return 0;
    try {
      const subject = path.orderedSubjects.find(s => s.id === subjectId || s.title === subjectId);
      const topics = Array.isArray(subject?.topics) ? subject.topics : [];
      const topicIds = topics.map(t => t.id || t.title);
      const completed = (progress.topicProgress || []).filter(tp => topicIds.includes(tp.topicId) && tp.lessonCompleted).length;
      return topicIds.length ? (completed / topicIds.length) * 100 : 0;
    } catch {
      return 0;
    }
  }

  function isSubjectUnlocked(subjectIndex) {
    if (!path || !path.orderedSubjects) return false;
    if (subjectIndex === 0) return true;
    const prev = path.orderedSubjects[subjectIndex - 1];
    const percent = subjectProgressPercent(prev.id);
    return percent >= 100;
  }

  function openSubject(subjectId) {
    navigate(`/study-paths/${pathId}/subject/${subjectId}`);
  }

  const orderedSubjects = Array.isArray(path?.orderedSubjects) ? path.orderedSubjects : [];
  const totalSubjects = orderedSubjects.length;
  const completedSubjects = orderedSubjects.filter((s) => subjectProgressPercent(s.id || s.title) >= 100).length;
  const totalProgress = totalSubjects > 0
    ? orderedSubjects.reduce((acc, s) => acc + subjectProgressPercent(s.id || s.title), 0) / totalSubjects
    : 0;
  const nextSubject = orderedSubjects.find((s, idx) => isSubjectUnlocked(idx) && subjectProgressPercent(s.id || s.title) < 100) || null;
  const nextSubjectId = nextSubject?.id || nextSubject?.title;

  function handleContinuePath() {
    if (!nextSubjectId) return;
    openSubject(nextSubjectId);
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Card
        sx={{
          mb: 3,
          borderRadius: UI_TOKENS.radius,
          color: '#fff',
          background: UI_TOKENS.heroGradient,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="h5" fontWeight={800}>{path.title}</Typography>
              {path.description ? (
                <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.92 }}>{path.description}</Typography>
              ) : null}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Chip label="Govt Exam Path" size="small" sx={{ bgcolor: UI_TOKENS.heroChipBg, color: UI_TOKENS.heroChipColor }} />
                <Chip label={`${completedSubjects}/${totalSubjects} subjects done`} size="small" sx={{ bgcolor: UI_TOKENS.heroChipBg, color: UI_TOKENS.heroChipColor }} />
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              <Button
                variant="contained"
                startIcon={<AnalyticsIcon />}
                onClick={() => navigate(`/study-paths/${pathId}/analytics`)}
                sx={{ ...UI_TOKENS.primaryButton, bgcolor: '#ffffff', color: '#1a237e', '&:hover': { bgcolor: '#f3f5ff' } }}
              >
                Analytics
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/study-paths/${pathId}/edit`)}
                sx={{ ...UI_TOKENS.primaryButton, borderColor: 'rgba(255,255,255,0.6)', color: '#fff' }}
              >
                Customize
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: UI_TOKENS.radius, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">Estimated Effort</Typography>
            <Typography variant="h6" fontWeight={800}>{(path.totalEstimatedHours || path.totalHours || 0)} hrs</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: UI_TOKENS.radius, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">Subjects</Typography>
            <Typography variant="h6" fontWeight={800}>{totalSubjects}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: UI_TOKENS.radius, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">Tasks</Typography>
            <Typography variant="h6" fontWeight={800}>{(path.tasks || []).length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: UI_TOKENS.radius, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">Overall Progress</Typography>
            <Typography variant="h6" fontWeight={800}>{Math.round(totalProgress)}%</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>Your Progress Roadmap</Typography>

        <Paper sx={{ p: 2, borderRadius: UI_TOKENS.radius, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Next Best Action</Typography>
              <Typography variant="body1" fontWeight={700}>
                {nextSubject ? `Continue ${nextSubject.title || nextSubject.id}` : 'Path complete. Start revision of completed subjects.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleContinuePath}
              disabled={!nextSubject}
              sx={{ ...UI_TOKENS.primaryButton, minWidth: 170 }}
            >
              {nextSubject ? 'Continue Path' : 'Completed'}
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ width: '100%', mt: 1 }}>
          <LinearProgress variant="determinate" value={totalProgress} sx={{ height: 10, borderRadius: '16px' }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
            {completedSubjects} of {totalSubjects} subjects completed
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={3}>
        {(path.orderedSubjects || []).map((s, idx) => (
          <Grid item xs={12} md={6} lg={4} key={s.id}>
            <SubjectTile subject={s} locked={!isSubjectUnlocked(idx)} percent={subjectProgressPercent(s.id)} onOpen={openSubject} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
